import React from "react";
import * as _ from "lodash";
import styled from "@emotion/styled";

type SortDirection = "asc" | "desc";

type SortBy = {
    key: string;
    direction: SortDirection;
};

type TransformObject<T extends object = any, U extends object = T> = (value: T, index?: number) => U;

const sortCollection = (collection: any[], sortBy: SortBy) => {
    const { key, direction } = sortBy;
    const sortedCollection = _.orderBy(collection, key, direction);
    return sortedCollection;
};

type SortIconProps = {
    keyName: string;
    sortBy: SortBy;
    setSortBy: React.Dispatch<React.SetStateAction<SortBy>>;
};

const SortIconTag = styled.i`
    position: absolute;
    top: 50%;
    font-size: ${(props: any) => props.size || 16}px;
    margin-left: 0.75rem;
    color: #828fab;
    transform: translateY(-50%);
    cursor: pointer;
    &.active {
        color: #00cbaf;
    }
`;

const SortIcon: React.FC<SortIconProps> = ({ sortBy, setSortBy, keyName }) => {
    const { direction } = sortBy;
    if (keyName !== sortBy.key) {
        return (
            <SortIconTag
                className="material-icons"
                onClick={() => {
                    setSortBy({ key: keyName, direction: "desc" });
                }}
            >
                unfold_more
            </SortIconTag>
        );
    }

    return direction === "asc" ? (
        <SortIconTag
            className="material-icons active"
            onClick={() => {
                setSortBy({ key: keyName, direction: "desc" });
            }}
        >
            expand_less
        </SortIconTag>
    ) : (
        <SortIconTag
            className="material-icons active"
            onClick={() => {
                setSortBy({ key: keyName, direction: "asc" });
            }}
        >
            expand_more
        </SortIconTag>
    );
};

type SortedCollectionWithButton<T> = {
    /** Sorted collection. */
    data: T[];

    /** Key used to sort the collection. */
    key: string;

    /** Direction that the collection was sorted. */
    direction: SortDirection;

    /** Function that can be called to return an icon button that can be clicked to sort the collection. */
    sortIcon: (
        /** Key to sort the collection by. */
        key: string
    ) => JSX.Element;
};

/**
 * Returns a sorted collection and a button that can be clicked to sort the collection.
 * @param collection Collection to be sorted.
 * @param transform Function to transform the collection before sorting.
 * @returns Returns a {@link SortedCollectionWithButton}.
 */
const useCollectSort = <T extends object = any, U extends object = T>(
    collection?: T[],
    transform?: TransformObject<T, U>
): SortedCollectionWithButton<U> => {
    const [sortBy, setSortBy] = React.useState<SortBy>({
        key: "",
        direction: "asc",
    });

    React.useEffect(() => {
        _.size(collection) && setSortBy({ key: "", direction: "asc" });
    }, [collection]);

    const transformedCollection = transform && collection ? collection.map(transform) : collection;
    const data = transformedCollection ? sortCollection(transformedCollection, sortBy) : [];

    const sortIcon = (key: string) => (
        <SortIcon keyName={key} sortBy={sortBy} setSortBy={setSortBy} />
    );

    return { data, key: sortBy.key, direction: sortBy.direction, sortIcon };
};

export default useCollectSort;
